import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Activity, RefreshCw, TrendingUp, TrendingDown } from "lucide-react";

const API_URL = process.env.REACT_APP_API_URL || process.env.REACT_APP_BACKEND_URL || "";

const SYMBOLS = ["BTC", "ETH", "SOL", "BNB", "XRP", "DOGE", "ADA", "AVAX", "LINK", "SUI"];

const LOGOS = {
  BTC:  "https://assets.coingecko.com/coins/images/1/small/bitcoin.png",
  ETH:  "https://assets.coingecko.com/coins/images/279/small/ethereum.png",
  SOL:  "https://assets.coingecko.com/coins/images/4128/small/solana.png",
  BNB:  "https://assets.coingecko.com/coins/images/825/small/bnb-icon2_2x.png",
  XRP:  "https://assets.coingecko.com/coins/images/44/small/xrp-symbol-white-128.png",
  DOGE: "https://assets.coingecko.com/coins/images/5/small/dogecoin.png",
  ADA:  "https://assets.coingecko.com/coins/images/975/small/cardano.png",
  AVAX: "https://assets.coingecko.com/coins/images/12559/small/Avalanche_Circle_RedWhite_Trans.png",
  LINK: "https://assets.coingecko.com/coins/images/877/small/chainlink-new-logo.png",
  SUI:  "https://assets.coingecko.com/coins/images/26375/small/sui_asset.jpeg",
};

function parsePct(s) {
  if (s == null) return 0;
  if (typeof s === "number") return s;
  const m = String(s).match(/-?[\d.]+/);
  return m ? parseFloat(m[0]) : 0;
}

function fundingTone(pct) {
  // pct is e.g. 0.0052 for 0.0052%
  const abs = Math.abs(pct);
  if (abs > 0.05) return pct > 0 ? "#ff5d6c" : "#4ade80"; // extreme bias
  if (abs > 0.02) return pct > 0 ? "#f5b042" : "#a3e635"; // notable bias
  return "rgba(255,255,255,0.4)"; // neutral
}

function fundingLabel(pct) {
  const abs = Math.abs(pct);
  if (abs > 0.05) return pct > 0 ? "LONGS PAY HEAVY" : "SHORTS PAY HEAVY";
  if (abs > 0.02) return pct > 0 ? "LONGS PAY" : "SHORTS PAY";
  if (abs > 0.005) return pct > 0 ? "Slight long bias" : "Slight short bias";
  return "Balanced";
}

function fmtVol(n) {
  if (n == null) return "—";
  if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
  return `$${n.toFixed(0)}`;
}

export default function DerivativesStrip() {
  const [data, setData] = useState({});  // { BTC: {funding_pct, ...}, ... }
  const [loading, setLoading] = useState(false);
  const [lastFetch, setLastFetch] = useState(null);
  const [sortBy, setSortBy] = useState("bias"); // bias | symbol

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const results = await Promise.all(
        SYMBOLS.map(sym =>
          fetch(`${API_URL}/api/intel/sentiment/${sym}`)
            .then(r => r.ok ? r.json() : null)
            .then(d => ({ sym, data: d }))
            .catch(() => ({ sym, data: null }))
        )
      );
      const next = {};
      results.forEach(({ sym, data: d }) => {
        if (!d) return;
        next[sym] = {
          symbol:    sym,
          sentiment: String(d.sentiment || "NEUTRAL"),
          score:     Number(d.score || 0),
          fundingPct: parsePct(d.funding?.funding_rate_pct),
          fundingInterp: String(d.funding?.interpretation || ""),
          fundingSource: String(d.funding?.source || ""),
          volume24h: Number(d.open_interest?.market_volume_24h || 0),
          marketTrend: String(d.open_interest?.market_trend || "NEUTRAL"),
          fgValue:   Number(d.fear_greed?.value || 50),
          signals:   Array.isArray(d.signals) ? d.signals : [],
        };
      });
      setData(next);
      setLastFetch(new Date());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
    const t = setInterval(fetchAll, 5 * 60 * 1000); // every 5 min
    return () => clearInterval(t);
  }, [fetchAll]);

  const rows = useMemo(() => {
    const list = Object.values(data);
    if (sortBy === "bias") {
      return list.sort((a, b) => Math.abs(b.fundingPct) - Math.abs(a.fundingPct));
    }
    return list.sort((a, b) => SYMBOLS.indexOf(a.symbol) - SYMBOLS.indexOf(b.symbol));
  }, [data, sortBy]);

  // Aggregate market bias from funding
  const aggregate = useMemo(() => {
    const list = Object.values(data);
    if (!list.length) return { dir: "—", avgPct: 0, bullishCount: 0, bearishCount: 0 };
    const avgPct = list.reduce((s, r) => s + r.fundingPct, 0) / list.length;
    const bullishCount = list.filter(r => r.fundingPct > 0.005).length;
    const bearishCount = list.filter(r => r.fundingPct < -0.005).length;
    const dir = avgPct > 0.005 ? "LONG-HEAVY"
              : avgPct < -0.005 ? "SHORT-HEAVY"
              : "BALANCED";
    return { dir, avgPct, bullishCount, bearishCount };
  }, [data]);

  const aggColor = aggregate.dir === "LONG-HEAVY" ? "#ff5d6c"
                : aggregate.dir === "SHORT-HEAVY" ? "#4ade80"
                : "rgba(255,255,255,0.4)";

  return (
    <section className="hc-panel hub-section-derivs">
      <header className="hc-panel-hdr">
        <Activity size={14} style={{ color: "#b377ff" }} />
        <span>Derivatives Pulse</span>
        <span className="hc-badge" style={{ color: aggColor, borderColor: `${aggColor}55`, background: `${aggColor}15` }}>
          {aggregate.dir}
        </span>
        <button type="button" className="hc-news-refresh" onClick={fetchAll} style={{ marginLeft: "auto" }}>
          <RefreshCw size={11} className={loading ? "hc-spin" : ""} />
        </button>
      </header>

      {/* Aggregate summary bar */}
      <div className="derivs-summary">
        <div className="derivs-summary-item">
          <span className="derivs-summary-lbl">AVG FUNDING</span>
          <span className="derivs-summary-val" style={{ color: aggColor }}>
            {aggregate.avgPct >= 0 ? "+" : ""}{aggregate.avgPct.toFixed(4)}%
          </span>
        </div>
        <div className="derivs-summary-item">
          <span className="derivs-summary-lbl">LONG BIAS</span>
          <span className="derivs-summary-val" style={{ color: "#ff5d6c" }}>{aggregate.bullishCount}</span>
        </div>
        <div className="derivs-summary-item">
          <span className="derivs-summary-lbl">SHORT BIAS</span>
          <span className="derivs-summary-val" style={{ color: "#4ade80" }}>{aggregate.bearishCount}</span>
        </div>
        <div className="derivs-summary-item">
          <span className="derivs-summary-lbl">BALANCED</span>
          <span className="derivs-summary-val">{rows.length - aggregate.bullishCount - aggregate.bearishCount}</span>
        </div>
        <button
          className="derivs-sort-btn"
          onClick={() => setSortBy(s => s === "bias" ? "symbol" : "bias")}
        >
          SORT · {sortBy === "bias" ? "BIAS" : "SYMBOL"}
        </button>
      </div>

      {/* Per-symbol rows */}
      <div className="derivs-list">
        {rows.length === 0 ? (
          <div className="hc-trade-empty">
            <span className="hc-empty-icon"><Activity size={16} /></span>
            <span>{loading ? "Loading derivatives…" : "Derivatives feed standing by"}</span>
            <small>{loading ? "Aggregating funding across tracked markets." : "Funding and open interest will populate when the feed reconnects."}</small>
          </div>
        ) : rows.map((r) => {
          const tone = fundingTone(r.fundingPct);
          const isLong = r.fundingPct > 0;
          const absPct = Math.abs(r.fundingPct);
          const barWidth = Math.min(absPct / 0.05 * 100, 100); // 0.05% = max
          return (
            <div key={r.symbol} className="derivs-row">
              <div className="derivs-row-left">
                <img src={LOGOS[r.symbol]} alt={r.symbol} width={16} height={16} style={{ borderRadius: "50%", flexShrink: 0 }} />
                <span className="derivs-row-sym">{r.symbol}</span>
              </div>

              {/* Funding rate bar with center origin */}
              <div className="derivs-bar-wrap">
                <div className="derivs-bar-track">
                  <div className="derivs-bar-axis" />
                  <div
                    className="derivs-bar-fill"
                    style={{
                      [isLong ? "left" : "right"]: "50%",
                      width: `${barWidth / 2}%`,
                      background: tone,
                    }}
                  />
                </div>
              </div>

              <span className="derivs-pct" style={{ color: tone }}>
                {isLong ? "+" : ""}{r.fundingPct.toFixed(4)}%
              </span>

              <span className="derivs-label" style={{ color: tone }}>
                {fundingLabel(r.fundingPct)}
              </span>

              <span className="derivs-vol">{fmtVol(r.volume24h)}</span>
            </div>
          );
        })}
      </div>
    </section>
  );
}
